import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { Settings, Save, RotateCcw, Percent, CreditCard, Building, Calculator } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminFinancialSettings() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    commissione_booking: 15.0,
    commissione_airbnb: 3.0,
    commissione_expedia: 15.0,
    commissione_direct: 0.0,
    iva_percentuale: 0.0,
    costo_transazione: 1.5,
    cedolare_secca: 21.0,
    altre_deduzioni: 0.0
  });

  useEffect(() => {
    if (token) {
      fetchSettings();
    }
  }, [token]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/financial/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching financial settings:', error);
      toast.error('Errore nel caricamento delle impostazioni');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/financial/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Impostazioni salvate con successo!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings({
      commissione_booking: 15.0,
      commissione_airbnb: 3.0,
      commissione_expedia: 15.0,
      commissione_direct: 0.0,
      iva_percentuale: 0.0,
      costo_transazione: 1.5,
      cedolare_secca: 21.0,
      altre_deduzioni: 0.0
    });
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  // Calculate example
  const exampleLordo = 100;
  const exampleCommissione = exampleLordo * (settings.commissione_booking / 100);
  const exampleTransazione = exampleLordo * (settings.costo_transazione / 100);
  const exampleNettoPre = exampleLordo - exampleCommissione - exampleTransazione;
  const exampleCedolare = exampleNettoPre * (settings.cedolare_secca / 100);
  const exampleNetto = exampleNettoPre - exampleCedolare;

  if (loading) {
    return (
      <AdminLayout title="Impostazioni Finanziarie">
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C5A059]"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Impostazioni Finanziarie">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-500">
            Configura le percentuali per calcolare il netto delle prenotazioni
          </p>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={handleReset}
              data-testid="reset-settings-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="bg-[#C5A059] hover:bg-[#B08D4A] text-white"
              data-testid="save-settings-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Salvataggio...' : 'Salva'}
            </Button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Commissioni OTA */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Building className="w-5 h-5 text-[#C5A059]" />
                Commissioni OTA
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="comm_booking" className="text-sm font-medium">
                  Booking.com (%)
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    id="comm_booking"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={settings.commissione_booking}
                    onChange={(e) => handleChange('commissione_booking', e.target.value)}
                    className="w-24"
                    data-testid="input-commissione-booking"
                  />
                  <Percent className="w-4 h-4 text-gray-400" />
                  <span className="text-xs text-gray-500 ml-2">Tipico: 15-18%</span>
                </div>
              </div>

              <div>
                <Label htmlFor="comm_airbnb" className="text-sm font-medium">
                  Airbnb Host (%)
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    id="comm_airbnb"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={settings.commissione_airbnb}
                    onChange={(e) => handleChange('commissione_airbnb', e.target.value)}
                    className="w-24"
                    data-testid="input-commissione-airbnb"
                  />
                  <Percent className="w-4 h-4 text-gray-400" />
                  <span className="text-xs text-gray-500 ml-2">Tipico: 3%</span>
                </div>
              </div>

              <div>
                <Label htmlFor="comm_expedia" className="text-sm font-medium">
                  Expedia / Altri OTA (%)
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    id="comm_expedia"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={settings.commissione_expedia}
                    onChange={(e) => handleChange('commissione_expedia', e.target.value)}
                    className="w-24"
                    data-testid="input-commissione-expedia"
                  />
                  <Percent className="w-4 h-4 text-gray-400" />
                  <span className="text-xs text-gray-500 ml-2">Tipico: 15-20%</span>
                </div>
              </div>

              <div>
                <Label htmlFor="comm_direct" className="text-sm font-medium">
                  Prenotazione Diretta (%)
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    id="comm_direct"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={settings.commissione_direct}
                    onChange={(e) => handleChange('commissione_direct', e.target.value)}
                    className="w-24"
                    data-testid="input-commissione-direct"
                  />
                  <Percent className="w-4 h-4 text-gray-400" />
                  <span className="text-xs text-gray-500 ml-2">Tipico: 0%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Costi e Tasse */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-[#C5A059]" />
                Costi Transazione e Tasse
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="costo_trans" className="text-sm font-medium">
                  Costo Transazione Carta (%)
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    id="costo_trans"
                    type="number"
                    step="0.1"
                    min="0"
                    max="10"
                    value={settings.costo_transazione}
                    onChange={(e) => handleChange('costo_transazione', e.target.value)}
                    className="w-24"
                    data-testid="input-costo-transazione"
                  />
                  <Percent className="w-4 h-4 text-gray-400" />
                  <span className="text-xs text-gray-500 ml-2">Stripe/PayPal: 1.5-2.9%</span>
                </div>
              </div>

              <div>
                <Label htmlFor="cedolare" className="text-sm font-medium">
                  Cedolare Secca (%)
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    id="cedolare"
                    type="number"
                    step="0.1"
                    min="0"
                    max="30"
                    value={settings.cedolare_secca}
                    onChange={(e) => handleChange('cedolare_secca', e.target.value)}
                    className="w-24"
                    data-testid="input-cedolare-secca"
                  />
                  <Percent className="w-4 h-4 text-gray-400" />
                  <span className="text-xs text-gray-500 ml-2">Standard: 21%</span>
                </div>
              </div>

              <div>
                <Label htmlFor="iva" className="text-sm font-medium">
                  IVA (se applicabile) (%)
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    id="iva"
                    type="number"
                    step="0.1"
                    min="0"
                    max="25"
                    value={settings.iva_percentuale}
                    onChange={(e) => handleChange('iva_percentuale', e.target.value)}
                    className="w-24"
                    data-testid="input-iva"
                  />
                  <Percent className="w-4 h-4 text-gray-400" />
                  <span className="text-xs text-gray-500 ml-2">0% se forfettario</span>
                </div>
              </div>

              <div>
                <Label htmlFor="altre" className="text-sm font-medium">
                  Altre Deduzioni (%)
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    id="altre"
                    type="number"
                    step="0.1"
                    min="0"
                    max="50"
                    value={settings.altre_deduzioni}
                    onChange={(e) => handleChange('altre_deduzioni', e.target.value)}
                    className="w-24"
                    data-testid="input-altre-deduzioni"
                  />
                  <Percent className="w-4 h-4 text-gray-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Example Calculation */}
        <Card className="bg-gradient-to-r from-[#C5A059]/5 to-[#C5A059]/10 border-[#C5A059]/20">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calculator className="w-5 h-5 text-[#C5A059]" />
              Esempio di Calcolo (Prenotazione Booking.com €100)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-5 gap-4 text-center">
              <div className="bg-white p-3 rounded-lg shadow-sm">
                <p className="text-xs text-gray-500 mb-1">Lordo</p>
                <p className="text-xl font-bold text-gray-900">€{exampleLordo.toFixed(2)}</p>
              </div>
              <div className="bg-white p-3 rounded-lg shadow-sm">
                <p className="text-xs text-gray-500 mb-1">- Commissione ({settings.commissione_booking}%)</p>
                <p className="text-xl font-bold text-red-600">-€{exampleCommissione.toFixed(2)}</p>
              </div>
              <div className="bg-white p-3 rounded-lg shadow-sm">
                <p className="text-xs text-gray-500 mb-1">- Transazione ({settings.costo_transazione}%)</p>
                <p className="text-xl font-bold text-red-600">-€{exampleTransazione.toFixed(2)}</p>
              </div>
              <div className="bg-white p-3 rounded-lg shadow-sm">
                <p className="text-xs text-gray-500 mb-1">- Cedolare ({settings.cedolare_secca}%)</p>
                <p className="text-xl font-bold text-orange-600">-€{exampleCedolare.toFixed(2)}</p>
              </div>
              <div className="bg-[#C5A059] p-3 rounded-lg shadow-sm">
                <p className="text-xs text-white/80 mb-1">= NETTO FINALE</p>
                <p className="text-xl font-bold text-white">€{exampleNetto.toFixed(2)}</p>
              </div>
            </div>
            <p className="text-xs text-gray-500 text-center mt-3">
              Formula: Netto = Lordo - Commissione OTA - Costo Transazione - Cedolare Secca
            </p>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
